import { Component, OnInit, ElementRef, ViewChild, AfterViewInit } from '@angular/core';
import { AbstractControl, Validators } from '@angular/forms';
import moment from 'moment';
import { FormComponentLookup, FormEntryComponentClass, validatorLookupRegistry, validatorRegisterLookup } from '@ptl/forms';
import { DateService } from '@ptl/global-components';
import { Observable, Subscription } from 'rxjs';
import { NgRedux, select } from '@ptl/angular-redux-store';

@FormComponentLookup('date-of-birth-input')
@Component({
  selector: 'app-date-of-birth-input',
  templateUrl: './date-of-birth-input.component.html',
  styleUrl: './date-of-birth-input.component.scss',
  standalone: false
})
export class DateOfBirthInputComponent extends FormEntryComponentClass implements OnInit, AfterViewInit {

  @ViewChild('entryRef') entryRef: ElementRef;
  dateRange = {
    min: '1900-01-01',
    minErrorFormat: '01/01/1900',
    max: '2200-01-01',
    maxErrorFormat: '01/01/2020',
    daysAgoDate: '01/01/2020'
  };
  moment = moment;
  isOkToDisplayAgeInfo: boolean = false; 
  effectiveDate: string;
  formatEffDate: string;

  constructor(
    private date: DateService
  ) {
    super();
  }

  ngOnInit() {
    if (this.entry?.formControlAttributes?.disabled) {
      this.formControls[this.entry?.formControlAttributes?.formControlName].disable();
    } else {
      this.formControls[this.entry?.formControlAttributes?.formControlName].enable();
    }
  
    let currentValue = this.reducerUtilityService.getStateFromPath(this.reducerUtilityService.getPayloadStates(), this.getFullStateDotPath());
  
    // Ensure value is actually a date so it displays correctly for date pickers
    if (currentValue) {
      this.resetFormValue(moment.utc(currentValue));
    }
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.setCustomAttributes();
  }

  registerCustomValidators() {
    validatorRegisterLookup('maxAgeManual')((control: AbstractControl) => {
      if (!(this.entry.custom && this.entry.custom.maxAge)) {
        console.warn('Add "maxAge" to custom configuration to get this validator to work');
        return null;
      }
      const maxValidDate = moment.utc().subtract(this.entry.custom.maxAge, 'years')
      const dateSelected = moment.utc(control.value)
      if (maxValidDate.isAfter(dateSelected)) {
        return { matDatepickerMax: { value: control.value } };
      }
      return null;
    });

    validatorRegisterLookup('minAgeManual')((control: AbstractControl) => {
      if (!(this.entry.custom && this.entry.custom.minAge)) {
        console.warn('Add "minAge" to custom configuration to get this validator to work');
        return null;
      }
      const minValidDate = moment.utc().subtract(this.entry.custom.minAge, 'years');
      const dateSelected = moment.utc(control.value);
      if (minValidDate.isBefore(dateSelected)) {
        return { matDatepickerMin: { value: control.value } };
      }
      return null;
    });

    validatorRegisterLookup('minDob')((control: AbstractControl) => {
      if (!(this.entry?.custom?.minDob)) {
        console.warn('Add "minDob" to custom configuration to get this validator to work');
        return null;
      }
      const pathArray = this.entry.custom.minDob?.split('.') || [];
      const payloadStates = this.reducerUtilityService.getPayloadStates();
      let targetState = payloadStates;
      for (let i = 0; i < pathArray.length; i++) {
        targetState = targetState[pathArray[i]];
      }
      const dobMoment = moment.utc(targetState);
      const valueMoment = moment.utc(control.value);
      const minDobDateIsAfterThusInvalid = dobMoment.isAfter(valueMoment, 'day');
      if (minDobDateIsAfterThusInvalid) {
        return { matDatepickerMinDob: { value: control.value } };
      }
      return null;
    });
  }

  getValue(value?: any) {
    if (value?.toLowerCase() === "today") {
      return new Date();
    }
    return value ? new Date(value) : value;
  }

  validatorSwitchHook(validatorItem) {
    switch (validatorItem.type) {
      case 'mask':
        this.entryMask = validatorItem.pattern;
        break;
      case 'date':
        this.custom.date = validatorItem.value;
        break;
      case 'required':
        this.valueRequired = true;
        return Validators.required;
      case 'pattern':
        this.pattern = validatorItem.value;
        break;
      case 'maxLength':
        this.maxLength = validatorItem.value;
        break;
      case 'max':
        this.dateRange.maxErrorFormat = this.parseDateReference(validatorItem.reference || '2200-01-01', true);
        this.dateRange.max = this.parseDateReference(validatorItem.reference || '2200-01-01');
        break;
      case 'min':
        this.dateRange.minErrorFormat = this.parseDateReference(validatorItem.reference || '1900-01-01', true);
        this.dateRange.min = this.parseDateReference(validatorItem.reference || '1900-01-01');
        break;
      case 'minAgeManual':
        this.dateRange.max = moment.utc().subtract(validatorItem.value, 'years').format('YYYY-MM-DD');
        this.dateRange.maxErrorFormat = moment.utc().subtract(validatorItem.value, 'years').format('MM/DD/YYYY');
        const minAgeValidator = validatorLookupRegistry.get(validatorItem.type);
        if (minAgeValidator) { return minAgeValidator; }
        break;
      case 'maxAgeManual':
        this.dateRange.min = moment.utc().subtract(validatorItem.value, 'years').format('YYYY-MM-DD');
        this.dateRange.minErrorFormat = moment.utc().subtract(validatorItem.value, 'years').format('MM/DD/YYYY');
        const maxAgeValidator = validatorLookupRegistry.get(validatorItem.type);
        if (maxAgeValidator) { return maxAgeValidator; }
        break;
      default:
        const customValidator = validatorLookupRegistry.get(validatorItem.type);
        if (customValidator) { return customValidator; }
    }
  }

  parseDateReference(dateRef, errorFormat = false) {
    const format = errorFormat ? 'MM/DD/YYYY' : 'YYYY-MM-DD';
    if (dateRef === 'today') {
      return moment.utc().format(format);
    } else {
      return moment.utc(dateRef).format(format);
    }
  }
  
  displayDateOfBirth(dobValue){
    if(dobValue && this.isOkToDisplayAgeInfo){
      const effDateAsMoment = moment.utc(this.effectiveDate);
      const dobAsMoment = moment.utc(dobValue)
      return effDateAsMoment.diff(dobAsMoment, 'years');
    }
  }

  setCustomAttributes(){
    
    if(this.entry?.custom?.displayAgeInfo){
      this.isOkToDisplayAgeInfo = this.entry.custom?.displayAgeInfo ? true : false;
      this.effectiveDate = this.reducerUtilityService.getPayloadStates().individualMarket.ratingInfo.effectiveDate;
      this.formatEffDate = moment.utc(this.effectiveDate).format('MMMM D, YYYY');
    }
    
    if (this.entry?.custom?.hideIfAgeLessThan) {
      const dependentDobStatePath = this.entry.formControlAttributes.connectPath + '.dob';
      const payloadStates = this.reducerUtilityService.getPayloadStates();
      const dependentDobState = this.reducerUtilityService.getStateFromPath(payloadStates, dependentDobStatePath);
      const today = this.date.getTodayAsMoment();
      const dependentMoment = this.date.getDateAsMoment(dependentDobState);
      const age = today.diff(dependentMoment, "years", true);
      
      if (age < this.entry.custom.hideIfAgeLessThan) {
        this.entry.base.display = false;
        this.entry.base.foreverHidden = true;
        delete this.entry.formControlAttributes.validators;
        this.setFormControlValidators();
      }
    }
    
  }
}
